import {
  FilterDocumentDateFilter,
  FilterDocumentDateFilterTypes,
  FilterOptionKeys,
  Goal,
  GoalFilterDocument,
  GoalImpactLevel,
  ID,
  Timestamp,
  WeekDays,
} from '@griply/types';
import { endOfDay, startOfDay } from 'date-fns';

import { calculateDeadlinePresetDates } from '../calculate-deadline-preset-dates';
import { goalNoImpactFilterValue } from '../constants/goal-list-page-options';
import { getSubGoals } from './get-sub-goals';
import { matchesDateFilter } from './matches-date-filter';

export type FilterGoalsByFilterDocumentOptions = {
  today: Timestamp;
  weekStartsOn: WeekDays;
};

const filterByLifeArea = (goals: Goal[], lifeAreaIds: ID[]): Goal[] => {
  if (!lifeAreaIds.length) {
    return [];
  }

  const validIds = new Set<ID>();
  const rootGoals = goals.filter(
    (goal) => goal.lifeAreaId && lifeAreaIds.includes(goal.lifeAreaId),
  );

  rootGoals.forEach((rootGoal) => {
    validIds.add(rootGoal.id);
    getSubGoals(rootGoal.id, { goals, recursive: true }).forEach((subGoal) =>
      validIds.add(subGoal.id),
    );
  });

  return goals.filter((goal) => validIds.has(goal.id));
};

const filterByGoal = (goals: Goal[], goalIds: ID[]): Goal[] => {
  if (!goalIds.length) {
    return [];
  }

  return goals.filter(
    (goal) =>
      goalIds.includes(goal.id) ||
      !!goal.parentIds?.some((parentId) => goalIds.includes(parentId)),
  );
};

// Resolves a date filter (preset or custom) to its [start, end] window.
const getDateFilterRange = (
  filter: FilterDocumentDateFilter,
  { today, weekStartsOn }: FilterGoalsByFilterDocumentOptions,
): { start?: Timestamp; end?: Timestamp } => {
  if (filter.type === FilterDocumentDateFilterTypes.Custom) {
    return {
      start: filter.custom?.start ? startOfDay(filter.custom.start) : undefined,
      end: filter.custom?.end ? endOfDay(filter.custom.end) : undefined,
    };
  }
  const range = calculateDeadlinePresetDates(filter.type, {
    today,
    weekStartsOn,
  });
  return { start: range?.strictStart ?? range?.start, end: range?.end };
};

// "Active goals" — keep goals whose active window [startDate, deadline] overlaps
// the selected range. Mirrors the task GoalActive filter (an unplanned goal,
// missing either date, is never active).
const filterByGoalActive = (
  goals: Goal[],
  filter: FilterDocumentDateFilter,
  options: FilterGoalsByFilterDocumentOptions,
): Goal[] => {
  const { start, end } = getDateFilterRange(filter, options);
  if (!start || !end) {
    return goals;
  }

  return goals.filter(
    (goal) =>
      !!goal.startDate &&
      !!goal.deadline &&
      goal.startDate <= end &&
      goal.deadline >= start,
  );
};

const filterByImpact = (goals: Goal[], include: string[]): Goal[] => {
  if (!include.length) {
    return goals;
  }

  const includeNoImpact = include.includes(goalNoImpactFilterValue);

  return goals.filter((goal) => {
    if (!goal.impact) {
      return includeNoImpact;
    }
    return include.includes(goal.impact as GoalImpactLevel);
  });
};

export const filterGoalsByFilterDocument = (
  goals: Goal[],
  filterDocument: GoalFilterDocument | undefined,
  { today, weekStartsOn }: FilterGoalsByFilterDocumentOptions,
): Goal[] => {
  const filters = filterDocument?.filters;
  if (!filters) {
    return goals;
  }

  let result = goals;

  const lifeAreaInclude = filters[FilterOptionKeys.LifeArea]?.include;
  if (lifeAreaInclude) {
    result = filterByLifeArea(result, lifeAreaInclude);
  }

  const goalInclude = filters[FilterOptionKeys.Goal]?.include;
  if (goalInclude) {
    result = filterByGoal(result, goalInclude);
  }

  const startDateFilter = filters[FilterOptionKeys.Date];
  if (startDateFilter?.type) {
    result = result.filter((goal) =>
      matchesDateFilter(goal.startDate, startDateFilter, {
        today,
        weekStartsOn,
      }),
    );
  }

  const deadlineFilter = filters[FilterOptionKeys.Deadline];
  if (deadlineFilter?.type) {
    result = result.filter((goal) =>
      matchesDateFilter(goal.deadline, deadlineFilter, { today, weekStartsOn }),
    );
  }

  const activeFilter = filters[FilterOptionKeys.GoalActive];
  if (activeFilter?.type) {
    result = filterByGoalActive(result, activeFilter, { today, weekStartsOn });
  }

  const impactInclude = filters[FilterOptionKeys.GoalImpact]?.include;
  if (impactInclude) {
    result = filterByImpact(result, impactInclude);
  }

  return result;
};
